// Lightweight client-side image watermarking and geotag helper
// Uses Canvas API only; works offline without network.

// Try to obtain a better GPS fix by watching for up to `timeout`.
// Resolves early if accuracy <= minAccuracy.
export async function getGeoLocation({
  enableHighAccuracy = true,
  timeout = 45000,
  maximumAge = 0,
  minAccuracy = 100,
} = {}) {
  if (typeof navigator === "undefined" || !navigator.geolocation) return null;

  return new Promise((resolve) => {
    let watchId = null;
    let timerId = null;
    let lastBest = null;

    const clearAll = () => {
      if (watchId != null) navigator.geolocation.clearWatch(watchId);
      if (timerId != null) clearTimeout(timerId);
    };

    const done = (val) => {
      clearAll();
      resolve(val || lastBest || null);
    };

    timerId = setTimeout(() => done(lastBest), timeout);

    try {
      watchId = navigator.geolocation.watchPosition(
        (pos) => {
          const { latitude, longitude, accuracy } = pos.coords || {};
          lastBest = {
            lat: latitude,
            lng: longitude,
            accuracy,
            timestamp: Date.now(),
          };
          if (typeof accuracy === "number" && accuracy <= minAccuracy) {
            done(lastBest);
          }
        },
        () => {
          // keep waiting until timeout; we might still get a later fix
        },
        { enableHighAccuracy, maximumAge }
      );
    } catch (e) {
      clearAll();
      resolve(null);
    }
  });
}

export async function watermarkImageFile(file, meta = {}, options = {}) {

  const { lat, lng, timestamp, locationName, city, district, kecamatan } = meta || {};
  const {
    quality = 0.92,
    maxWidth = null,
    logoUrl = null,
    logoSize = null, // px; if null compute relative to image
    includeMap = false,
    mapZoom = 16,
    mapSize = null, // px; if null compute relative to image
    showLatLon = true,
  } = options || {};

  const imgSrc = URL.createObjectURL(file);
  try {
    const img = await loadImage(imgSrc);

    // Determine canvas size (optionally constrain width)
    let targetW = img.naturalWidth || img.width;
    let targetH = img.naturalHeight || img.height;
    if (maxWidth && targetW > maxWidth) {
      const scale = maxWidth / targetW;
      targetW = Math.round(targetW * scale);
      targetH = Math.round(targetH * scale);
    }

    const canvas = document.createElement("canvas");
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, targetW, targetH);

    // Build watermark text lines
    const latTxt = typeof lat === "number" ? lat.toFixed(6) : "N/A";
    const lngTxt = typeof lng === "number" ? lng.toFixed(6) : "N/A";
    const dateObj = timestamp ? new Date(timestamp) : new Date();
    const timeTxt = dateObj.toLocaleString();

    const lines = [];
    if (locationName) lines.push(`Loc: ${locationName}`);
    if (showLatLon) lines.push(`Lat: ${latTxt}, Lon: ${lngTxt}`);
    lines.push(`Time: ${timeTxt}`);
    const dist = district || kecamatan;
    const addrParts = [];

    if (city) addrParts.push(`Kota: ${city}`);
    if (dist) addrParts.push(`Kec: ${dist}`);
    if (addrParts.length) lines.push(addrParts.join("  |  "));

    // Style sizing relative to image width
    const baseFont = Math.max(14, Math.round(targetW / 40)); // ~2.5% of width
    ctx.font = `${baseFont}px sans-serif`;
    ctx.textBaseline = "top";
    ctx.textAlign = "left";

    // Measure text box
    const lineHe = Math.round(baseFont * 1.35);
    const padding = Math.round(baseFont * 0.5);
    const margin = Math.max(10, Math.round(baseFont * 0.6));
    const textWidth = Math.max(...lines.map((t) => ctx.measureText(t).width));
    const textHeight = lineHe * lines.length;

    const boxX = margin;
    const boxY = targetH - textHeight - padding * 2 - margin;
    const boxW = textWidth + padding * 2;
    const boxH = textHeight + padding * 2;

    // Draw background box for readability
    ctx.fillStyle = "rgba(0,0,0,0.45)";
    ctx.fillRect(boxX - 2, boxY - 2, boxW + 4, boxH + 4);

    // Draw text lines
    ctx.fillStyle = "#ffffff";
    lines.forEach((txt, i) => {
      ctx.fillText(txt, boxX + padding, boxY + padding + i * lineHe);
    });

    // Optional: draw logo (top-left)
    if (logoUrl) {
      try {
        const logoImg = await loadImageCORS(logoUrl);
        const maxLogo = logoSize || Math.max(60, Math.round(targetW / 20));
        const aspect = logoImg.naturalWidth / logoImg.naturalHeight || 1;
        const lw = maxLogo;
        const lh = Math.round(maxLogo / aspect);
        const lx = margin;
        const ly = margin;
        // Background for contrast
        ctx.fillStyle = "rgba(255,255,255,0.65)";
        ctx.fillRect(lx - 6, ly - 6, lw + 12, lh + 12);
        ctx.drawImage(logoImg, lx, ly, lw, lh);
      } catch (e) {
        // ignore logo errors
      }
    }

    // Optional: draw mini map (top-right)
    if (includeMap && showLatLon && typeof lat === "number" && typeof lng === "number") {
      const mSize = mapSize || Math.max(150, Math.round(targetW / 6));
      const mx = targetW - mSize - margin;
      const my = margin;
      try {
        const tile = await getSingleOSMTile(lat, lng, mapZoom);
        if (tile) {
          // draw tile scaled to mSize x mSize
          ctx.drawImage(tile.image, mx, my, mSize, mSize);
          // compute marker position within this tile image
          const px = tile.pixelXInTile;
          const py = tile.pixelYInTile;
          const sx = mx + (px / 256) * mSize;
          const sy = my + (py / 256) * mSize;
          // draw location pin marker
          const pinBase = Math.max(2, Math.round(mSize / 50));
          drawLocationPin(ctx, sx, sy, pinBase * 2);
          // frame + attribution
          ctx.strokeStyle = "rgba(255,255,255,0.9)";
          ctx.lineWidth = 2;
          ctx.strokeRect(mx - 1, my - 1, mSize + 2, mSize + 2);
          const attrFont = Math.max(10, Math.round(baseFont * 0.7));
          ctx.font = `${attrFont}px sans-serif`;
          ctx.fillStyle = "rgba(0,0,0,0.6)";
          const attr = "© OpenStreetMap";
          ctx.fillRect(mx, my + mSize - attrFont - 6, ctx.measureText(attr).width + 8, attrFont + 6);
          ctx.fillStyle = "#fff";
          ctx.fillText(attr, mx + 4, my + mSize - attrFont - 2);
        } else {
          drawMapFallback(ctx, mx, my, mSize, mSize);
        }
      } catch (e) {
        drawMapFallback(ctx, mx, my, mSize, mSize);
      }
    }

    const blob = await canvasToBlob(canvas, "image/jpeg", quality);
    const watermarkedFile = new File([blob], renameWithSuffix(file.name, "wm", "jpg"), {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
    const objectUrl = URL.createObjectURL(watermarkedFile);
    return { file: watermarkedFile, objectUrl };
  } finally {
    URL.revokeObjectURL(imgSrc);
  }
}

// Optional EXIF GPS extraction from image file (offline)
export async function extractGpsFromImage(file) {
  try {
    // Lazy import to avoid bundling if unused
    const { default: exifr } = await import("exifr");
    const data = await exifr.parse(file, [
      "latitude",
      "longitude",
      "GPSDateStamp",
      "GPSTimeStamp",
    ]);
    if (data && typeof data.latitude === "number" && typeof data.longitude === "number") {
      return { lat: data.latitude, lng: data.longitude, timestamp: Date.now() };
    }
  } catch (e) {
    // ignore
  }
  return null;
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function loadImageCORS(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("image load failed"));
    img.src = src;
  });
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve, reject) => {
    if (canvas.toBlob) {
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("Blob conversion failed"))), type, quality);
    } else {
      // Fallback for very old browsers
      const dataUrl = canvas.toDataURL(type, quality);
      const blob = dataURLToBlob(dataUrl);
      if (blob) resolve(blob);
      else reject(new Error("Blob conversion failed"));
    }
  });
}

function dataURLToBlob(dataUrl) {
  const arr = dataUrl.split(",");
  const mimeMatch = arr[0].match(/:(.*?);/);
  const mime = (mimeMatch && mimeMatch[1]) || "image/jpeg";
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) u8arr[n] = bstr.charCodeAt(n);
  return new Blob([u8arr], { type: mime });
}

function renameWithSuffix(filename, suffix, forceExt) {
  const dot = filename.lastIndexOf(".");
  const name = dot > 0 ? filename.slice(0, dot) : filename;
  const ext = forceExt || (dot > 0 ? filename.slice(dot + 1) : "jpg");
  return `${name}_${suffix}.${ext}`;
}

// --- Mini OpenStreetMap helpers ---
function lonLatToTileXY(lon, lat, zoom) {
  const x = Math.floor(((lon + 180) / 360) * Math.pow(2, zoom));
  const y = Math.floor(
    (
      (1 -
        Math.log(Math.tan((lat * Math.PI) / 180) + 1 / Math.cos((lat * Math.PI) / 180)) /
        Math.PI) /
      2
    ) * Math.pow(2, zoom)
  );
  return { x, y };
}

function lonLatToPixelInTile(lon, lat, zoom) {
  const n = Math.pow(2, zoom);
  const xtile = ((lon + 180) / 360) * n;
  const latRad = (lat * Math.PI) / 180;
  const ytile = ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n;
  const px = Math.floor((xtile - Math.floor(xtile)) * 256);
  const py = Math.floor((ytile - Math.floor(ytile)) * 256);
  return { px, py, tileX: Math.floor(xtile), tileY: Math.floor(ytile) };
}

async function getSingleOSMTile(lat, lng, zoom) {
  try {
    const { tileX, tileY, px, py } = lonLatToPixelInTile(lng, lat, zoom);
    const url = `https://tile.openstreetmap.org/${zoom}/${tileX}/${tileY}.png`;
    const image = await loadImageCORS(url);
    return { image, pixelXInTile: px, pixelYInTile: py };
  } catch (e) {
    return null;
  }
}

function drawMapFallback(ctx, x, y, w, h) {
  ctx.fillStyle = "#f0f0f0";
  ctx.fillRect(x, y, w, h);
  ctx.strokeStyle = "#bbb";
  ctx.strokeRect(x, y, w, h);
  ctx.fillStyle = "#666";
  const msg = "Map offline";
  const size = Math.max(10, Math.round(w / 10));
  ctx.font = `${size}px sans-serif`;
  const tw = ctx.measureText(msg).width;
  ctx.fillText(msg, x + (w - tw) / 2, y + h / 2 - size / 2);
}

// Draw a simple location pin with tip at (x,y)
function drawLocationPin(ctx, x, y, size) {
  const r = size; // base radius for head
  ctx.save();
  // tail (triangle)
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.lineTo(x - r * 0.6, y - r * 0.7);
  ctx.lineTo(x + r * 0.6, y - r * 0.7);
  ctx.closePath();
  ctx.fillStyle = "#d32f2f";
  ctx.strokeStyle = "#ffffff";
  ctx.lineWidth = Math.max(1, Math.round(r * 0.2));
  ctx.fill();
  ctx.stroke();
  // head (circle)
  ctx.beginPath();
  ctx.arc(x, y - r * 1.2, r, 0, Math.PI * 2);
  ctx.fillStyle = "#d32f2f";
  ctx.fill();
  ctx.stroke();
  // inner hole
  ctx.beginPath();
  ctx.arc(x, y - r * 1.2, r * 0.5, 0, Math.PI * 2);
  ctx.fillStyle = "#ffffff";
  ctx.fill();
  ctx.restore();
}
